import SellingUnit from "@/components/SellingUnit";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, CreditCard, ExternalLink, Loader2, Lock, Pencil, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang, useL } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, money, apiError } from "@/lib/api";
import { toast } from "sonner";
import { shippingName, ShippingEstimate } from "@/components/checkout/ShippingDetails";
import PayPalPayment from "@/components/checkout/PayPalPayment";
import { CardBrandRow, PayPalMark } from "@/components/checkout/PaymentBrandIcons";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","VI","GU","AS","MP","AA","AE","AP"];
const inputCls = "w-full rounded-xl border border-border bg-cream-card px-4 py-3 text-sm outline-none transition focus:border-gold disabled:cursor-not-allowed disabled:opacity-60";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyAddress = { firstName: "", lastName: "", line1: "", line2: "", city: "", state: "", zip: "" };

function AddressFields({ value, onChange, disabled, testPrefix }) {
  const { t } = useLang();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <input required disabled={disabled} placeholder={t("checkout.firstName")} value={value.firstName} onChange={set("firstName")} className={inputCls} data-testid={`${testPrefix}-first-name`} />
      <input required disabled={disabled} placeholder={t("checkout.lastName")} value={value.lastName} onChange={set("lastName")} className={inputCls} data-testid={`${testPrefix}-last-name`} />
      <input required disabled={disabled} placeholder={t("checkout.addressLine1")} value={value.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} data-testid={`${testPrefix}-line1`} />
      <input disabled={disabled} placeholder={t("checkout.addressLine2")} value={value.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} data-testid={`${testPrefix}-line2`} />
      <input required disabled={disabled} placeholder={t("checkout.city")} value={value.city} onChange={set("city")} className={inputCls} data-testid={`${testPrefix}-city`} />
      <select required disabled={disabled} value={value.state} onChange={set("state")} className={inputCls} data-testid={`${testPrefix}-state`}>
        <option value="">{t("checkout.state")}</option>
        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <input required disabled={disabled} placeholder={t("checkout.zip")} value={value.zip} onChange={set("zip")} pattern="\d{5}(-\d{4})?" className={inputCls} data-testid={`${testPrefix}-zip`} />
      <input disabled value={t("shipping.country")} className={`${inputCls} bg-cream text-navy/50`} data-testid={`${testPrefix}-country`} />
    </div>
  );
}

function isAddressComplete(a) {
  return !!(a.firstName.trim() && a.lastName.trim() && a.line1.trim() && a.city.trim() && a.state && /^\d{5}(-\d{4})?$/.test(a.zip.trim()));
}

function toApiAddress(a, phone = "") {
  return {
    full_name: `${a.firstName.trim()} ${a.lastName.trim()}`.trim(),
    line1: a.line1.trim(), line2: a.line2.trim(), city: a.city.trim(),
    state: a.state, zip: a.zip.trim(), phone: phone.trim(), country: "US",
  };
}

export default function Checkout() {
  const { items, subtotal, coupon } = useCart();
  const { t, lang } = useLang();
  const L = useL();
  const { user } = useAuth();
  const navigate = useNavigate();
  const paymentSectionRef = useRef(null);

  const [quote, setQuote] = useState(null);
  const [method, setMethod] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [shippingError, setShippingError] = useState("");
  const quoteRequest = useRef(0);
  const rateController = useRef(null);
  const [clock, setClock] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);

  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [shipAddr, setShipAddr] = useState({ ...emptyAddress, firstName: user?.name?.split(" ")[0] || "", lastName: user?.name?.split(" ").slice(1).join(" ") || "" });
  const [billingSame, setBillingSame] = useState(true);
  const [billAddr, setBillAddr] = useState(emptyAddress);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const contactTouched = useRef(false);
  const contactOwner = useRef(user?.id || null);
  useEffect(() => {
    const identity = user?.id || null;
    if (contactOwner.current !== identity) {
      contactOwner.current = identity;
      contactTouched.current = false;
      setEmail(user?.email || ''); setPhone(user?.phone || '');
      setShipAddr({...emptyAddress, firstName:user?.name?.split(' ')[0] || '', lastName:user?.name?.split(' ').slice(1).join(' ') || ''});
      setBillAddr({...emptyAddress}); setBillingSame(true);
      return;
    }
    if (!user || contactTouched.current) return;
    setEmail(old => old || user.email || '');
    setPhone(old => old || user.phone || '');
  }, [user]);

  const [phase, setPhase] = useState("form"); // form | payment
  const [order, setOrder] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('kb_pending_payment') || 'null'); } catch { return null; }
  });
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0); // bump to remount payment widget on retry

  const cartKey = JSON.stringify(items.map(({ product_id, quantity }) => ({ product_id, quantity })));
  const destinationKey = JSON.stringify(toApiAddress(shipAddr, ""));
  const requestKey = JSON.stringify([cartKey, destinationKey, coupon?.code || null]);
  const currentRequestKey = useRef(requestKey);
  currentRequestKey.current = requestKey;
  const usableQuote = quote?.requestKey === requestKey && Date.parse(quote.expires_at) > clock ? quote : null;
  const methods = usableQuote?.options || [];
  const selected = methods.find((m) => m.id === method);
  const destinationValid = isAddressComplete(shipAddr);

  useEffect(() => {
    quoteRequest.current += 1;
    rateController.current?.abort(); rateController.current = null;
    setQuote(null); setShippingError(""); setCalculating(false);
    setOrder(null); setPhase("form");
    return () => rateController.current?.abort();
  }, [requestKey]);
  useEffect(() => {
    if (!quote) return;
    const timer = setTimeout(() => { setClock(Date.now()); setShippingError("shipping.quote_expired"); }, Math.max(0, Date.parse(quote.expires_at) - Date.now()));
    return () => clearTimeout(timer);
  }, [quote]);
  useEffect(() => { if (items.length === 0 && phase === "form") navigate("/shop"); }, [items.length, phase, navigate]);

  const calculateShipping = async () => {
    if (usableQuote?.options?.length) return; // Reuse only the still-valid, exact cart/address quote.
    if (!destinationValid) { setShippingError("shipping.invalid_zip"); return; }
    if (rateController.current && !rateController.current.signal.aborted) return;
    const controller = new AbortController(); rateController.current = controller;
    const sequence = ++quoteRequest.current;
    setCalculating(true); setQuote(null); setOrder(null); setShippingError("");
    try {
      const { data } = await api.post("/shipping/rates", {
        items: JSON.parse(cartKey), destination: JSON.parse(destinationKey), coupon_code: coupon?.code || null,
      }, { timeout: 30000, signal: controller.signal });
      if (sequence !== quoteRequest.current || currentRequestKey.current !== requestKey) return;
      setClock(Date.now());
      setQuote({ ...data, requestKey });
      setMethod((previous) => data.options.some((m) => m.id === previous) ? previous : data.options[0]?.id || "");
    } catch (err) {
      if (sequence === quoteRequest.current && currentRequestKey.current === requestKey) {
        const code = err.response?.data?.detail;
        const setup = ['shipping.grid_product_review','shipping.invalid_grid','shipping.verification_required','shipping.missing_measurements','shipping.profile_incomplete','shipping.readiness_incomplete','shipping.approval_required','shipping.profile_requires_easypost','shipping.package_unavailable'];
        setShippingError(setup.includes(code) ? 'shipping.configuration_pending' : typeof code === "string" && code.startsWith("shipping.") ? code : "shipping.unavailable");
      }
    } finally { if (rateController.current === controller) rateController.current = null; if (sequence === quoteRequest.current) setCalculating(false); }
  };

  useEffect(() => {
    if (!destinationValid || !items.length || phase !== 'form') return;
    const timer = setTimeout(() => calculateShipping(), 700);
    return () => clearTimeout(timer);
    // Changes to cart/address invalidate the previous request; no retry loop on errors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, destinationValid]);

  const display = order
    ? { subtotal: order.subtotal, shipping: order.shipping, tax: order.tax, discount: order.discount, total: order.total }
    : selected?.summary || { subtotal, shipping: null, tax: 0, discount: 0, total: null };

  const formValid = useMemo(() => {
    if (pendingPayment) return false;
    if (!EMAIL_RE.test(email.trim()) || !selected || calculating) return false;
    if (!isAddressComplete(shipAddr)) return false;
    if (!billingSame && !isAddressComplete(billAddr)) return false;
    return true;
  }, [email, shipAddr, billingSame, billAddr, selected, calculating, pendingPayment]);

  const resumePayment = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/payments/stripe/create-checkout-session', { order_id: pendingPayment.id },
        { headers: { 'X-Order-Token': pendingPayment.token } });
      window.location.href = data.checkout_url;
    } catch (err) {
      const code = err.response?.data?.detail;
      if (code === 'payment.already_paid') {
        sessionStorage.removeItem('kb_pending_payment');
        navigate(`/order/${pendingPayment.id}?token=${encodeURIComponent(pendingPayment.token)}`);
      } else if (code === 'payment.attempt_closed' || code === 'payment.stock_unavailable') {
        sessionStorage.removeItem('kb_pending_payment'); setPendingPayment(null); setQuote(null); setOrder(null);
      }
      toast.error(apiError(code, lang)); setSubmitting(false);
    }
  };

  const ctaLabel = paymentMethod === "card" ? t("checkout.continueToSecurePayment") : t("checkout.continueToPayment");

  const continueToPayment = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error(t("checkout.emptyCartError")); return; }
    if (!formValid) { toast.error(t("common.error")); return; }
    setSubmitting(true);
    try {
      const orderRes = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_address: toApiAddress(shipAddr, phone),
        billing_address: billingSame ? null : toApiAddress(billAddr, phone),
        shipping_method: method,
        shipping_quote_id: usableQuote.quote_id,
        payment_method: "card",
        email,
        coupon_code: coupon?.code || null,
        lang,
      });
      const newOrder = orderRes.data;
      setOrder(newOrder);
      const receipt = { id: newOrder.id, token: newOrder.guest_token, total: newOrder.total };
      setPendingPayment(receipt);
      try { sessionStorage.setItem('kb_pending_payment', JSON.stringify(receipt)); } catch {}
      setPaymentFailed(false);

      if (paymentMethod === "card") {
        // Card payment happens entirely on Stripe's own hosted Checkout
        // page, not embedded here -- the browser leaves Kreyola Boutik and
        // Stripe redirects back to /payment/success (or /payment/cancel)
        // once the customer is done. Leave the button in its loading state
        // rather than resetting it, since resetting here would just flash
        // right before the actual navigation happens.
        const { data } = await api.post("/payments/stripe/create-checkout-session", { order_id: newOrder.id }, { headers: { "X-Order-Token": newOrder.guest_token } });
        window.location.href = data.checkout_url;
        return;
      }

      setPhase("payment");
      setSubmitting(false);
      setTimeout(() => paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      const code = err.response?.data?.detail;
      if (typeof code === "string" && code.startsWith("shipping.")) {
        setQuote(null); setOrder(null); setShippingError(code); toast.error(t(code));
      } else toast.error(apiError(code, lang));
      setSubmitting(false);
    }
  };

  const editDetails = () => { setPhase("form"); setOrder(null); setPaymentFailed(false); };

  // PayPal's onApprove firing is only a cue to go check status -- the
  // success page is what asks the backend to authoritatively confirm before
  // showing "paid", and is also the only place the cart gets cleared (never
  // here -- a callback firing is not proof of payment). Card payment doesn't
  // need an equivalent handler: it never returns to this component at all,
  // since the browser navigates straight to Stripe's hosted page and Stripe
  // itself redirects to /payment/success afterward.
  const onPaypalSuccess = (orderId) => {
    // Unlike Stripe (a full external redirect, so the guest access token has
    // to be handed back through the return URL -- see server.py), PayPal
    // never navigates away from this page, so the token already sitting in
    // `order` state from the original POST /orders response is still good.
    const q = new URLSearchParams({ order_id: orderId || order.id });
    if (order?.guest_token) q.set("token", order.guest_token);
    navigate(`/payment/success?${q.toString()}`);
  };
  const onPaymentFailure = () => setPaymentFailed(true);
  const retryPayment = () => { setPaymentFailed(false); setPaymentKey((k) => k + 1); };

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-8 sm:py-10" data-testid="checkout-page">
      <h1 className="mb-2 font-display text-3xl font-bold text-navy sm:text-4xl">{t("checkout.title")}</h1>
      <p className="mb-6 text-sm text-sunset">{t("checkout.usOnly")}</p>
      {pendingPayment && <div className="mb-6 rounded-xl border border-gold bg-cream p-4" data-testid="resume-payment">
        <p>{lang === 'fr' ? 'Une commande a déjà été acceptée. Reprenez son paiement sans créer une autre commande.' : 'An order has already been accepted. Resume its payment without creating another order.'}</p>
        <p className="my-2 font-semibold">{lang === 'fr' ? 'Total accepté : ' : 'Accepted total: '}{money(pendingPayment.total)}</p>
        <a className="mr-4 underline" href={`/order/${pendingPayment.id}?token=${encodeURIComponent(pendingPayment.token)}`}>{lang === 'fr' ? 'Voir la commande' : 'View order'}</a>
        <button type="button" disabled={submitting} onClick={resumePayment} className="btn-gold rounded-full px-4 py-2">{lang === 'fr' ? 'Reprendre le paiement' : 'Resume payment'}</button>
      </div>}

      {/* Mobile: collapsed order summary, does not push the form down */}
      <div className="mb-6 rounded-2xl border border-border bg-cream-card card-shadow lg:hidden">
        <button type="button" onClick={() => setMobileSummaryOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3.5" data-testid="mobile-summary-toggle">
          <span className="flex items-center gap-2 text-sm font-semibold text-navy">
            <ChevronDown size={16} className={`transition-transform ${mobileSummaryOpen ? "rotate-180" : ""}`} />
            {mobileSummaryOpen ? t("checkout.hideSummary") : t("checkout.showSummary")} ({items.reduce((s, i) => s + i.quantity, 0)} {t("checkout.itemsCount")})
          </span>
          <span className="font-head text-lg font-bold text-navy">{display.total == null ? "\u2014" : money(display.total)}</span>
        </button>
        {mobileSummaryOpen && (
          <div className="border-t border-border px-4 pb-4 pt-3">
            <OrderSummaryBody items={order?.items || usableQuote?.items || items} L={L} t={t} display={display} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]">
        <form id="checkout-form" onInput={() => { contactTouched.current = true; }} onSubmit={continueToPayment} className="min-w-0 space-y-6">
          <fieldset disabled={phase === "payment"} className="min-w-0 space-y-6">
            <section className="rounded-2xl border border-border bg-cream-card p-5 card-shadow sm:p-6">
              <h2 className="mb-4 font-head text-lg font-bold text-navy">{t("checkout.contact")}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required type="email" placeholder={t("common.email")} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="checkout-email" />
                <input placeholder={t("checkout.phoneOptional")} value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} data-testid="checkout-phone" />
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-cream-card p-5 card-shadow sm:p-6">
              <h2 className="mb-4 font-head text-lg font-bold text-navy">{t("checkout.address")}</h2>
              <AddressFields value={shipAddr} onChange={setShipAddr} disabled={phase === "payment"} testPrefix="ship" />
            </section>

            <section className="rounded-2xl border border-border bg-cream-card p-5 card-shadow sm:p-6">
              <h2 className="mb-4 font-head text-lg font-bold text-navy">{t("checkout.shippingMethod")}</h2>
              <p className="mb-3 text-sm text-muted-foreground">{t("shipping.enterZip")}</p>
              <button type="button" onClick={calculateShipping} disabled={calculating || !!usableQuote?.options?.length} className="btn-gold mb-4 flex min-h-11 items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-60" data-testid="calculate-shipping">
                {calculating && <Loader2 size={16} className="animate-spin" />}{usableQuote?.options?.length ? (lang === 'fr' ? 'Frais de livraison à jour' : 'Shipping rates up to date') : t(calculating ? "shipping.calculating" : "shipping.calculate")}
              </button>
              <div aria-live="polite" aria-busy={calculating}>
                <p className="mb-3 text-xs text-muted-foreground">{lang === "fr" ? "Les frais affichés incluent la préparation éventuelle. Aucun supplément ne sera ajouté après validation." : "Displayed shipping includes any handling charge. No surcharge will be added after confirmation."}</p>
                {shippingError && <p role="alert" className="mb-3 text-sm text-red-600" data-testid="shipping-error">{t(shippingError)}</p>}
                {usableQuote?.notice && <p className="mb-3 text-sm text-sunset">{t(usableQuote.notice)}</p>}
                {selected?.rate_source === "fallback" && <p className="mb-3 text-xs text-muted-foreground">{t("shipping.fallbackNotice")}</p>}
              </div>
              <div className="space-y-3">
                {methods.map((m) => (
                  <label key={m.id} className={`flex min-h-16 cursor-pointer items-center justify-between gap-3 rounded-xl border-2 p-4 transition ${method === m.id ? "border-gold bg-gold/5" : "border-border"}`} data-testid={`shipping-${m.id}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <input type="radio" name="ship" checked={method === m.id} onChange={() => { setMethod(m.id); setOrder(null); }} className="shrink-0 accent-gold" />
                      <div className="min-w-0 text-sm font-medium text-navy">{shippingName(m, null, t)}<ShippingEstimate details={m} /></div>
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-navy">{m.amount === 0 ? t("common.free") : money(m.amount)}</span>
                  </label>
                ))}
              </div>
              {methods.length > 0 && <p className="mt-3 text-xs text-muted-foreground">{t("shipping.servicesNotice")}</p>}
            </section>

            <section className="rounded-2xl border border-border bg-cream-card p-5 card-shadow sm:p-6">
              <h2 className="mb-1 font-head text-lg font-bold text-navy">{t("checkout.billingAddress")}</h2>
              <label className="mt-3 flex items-center gap-2 text-sm text-navy">
                <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} className="accent-gold" data-testid="billing-same-checkbox" />
                {t("checkout.billingSame")}
              </label>
              {!billingSame && (
                <div className="mt-4">
                  <AddressFields value={billAddr} onChange={setBillAddr} disabled={phase === "payment"} testPrefix="bill" />
                </div>
              )}
            </section>
          </fieldset>

          <section ref={paymentSectionRef} className="rounded-2xl border border-border bg-cream-card p-5 card-shadow sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-head text-lg font-bold text-navy">{t("checkout.paymentMethod")}</h2>
              {phase === "payment" && (
                <button type="button" onClick={editDetails} className="flex items-center gap-1 text-xs font-semibold text-ocean" data-testid="edit-details-btn">
                  <Pencil size={13} /> {t("common.continue") === "Continue" ? "Edit" : "Modifier"}
                </button>
              )}
            </div>

            <div className="space-y-3">
              <label className={`flex cursor-pointer items-center justify-between rounded-xl border-2 p-4 transition ${paymentMethod === "card" ? "border-gold bg-gold/5" : "border-border"}`} data-testid="payment-method-card">
                <div className="flex items-center gap-3">
                  <input type="radio" name="pm" checked={paymentMethod === "card"} onChange={() => setPaymentMethod("card")} className="accent-gold" />
                  <div>
                    <p className="text-sm font-semibold text-navy">{t("checkout.card")}</p>
                    <p className="text-xs text-muted-foreground">{t("checkout.cardDesc")}</p>
                  </div>
                </div>
                <CardBrandRow />
              </label>

              {paymentMethod === "card" && (
                <p className="flex items-start gap-2 rounded-xl bg-cream px-4 py-3 text-xs text-navy/70" data-testid="stripe-redirect-note">
                  <ExternalLink size={14} className="mt-0.5 shrink-0 text-navy/40" />
                  {t("checkout.stripeRedirectNote")}
                </p>
              )}

              <label className="flex cursor-not-allowed items-center justify-between rounded-xl border-2 border-border p-4 opacity-60" data-testid="payment-method-paypal">
                <div className="flex items-center gap-3">
                  <input type="radio" name="pm" checked={false} disabled aria-disabled="true" className="accent-gold" />
                  <div>
                    <p className="text-sm font-semibold text-navy">{t("checkout.paypal")}</p>
                    <p className="text-xs text-muted-foreground">{lang === "fr" ? "Bientôt disponible" : "Coming Soon"}</p>
                  </div>
                </div>
                <PayPalMark className="h-6 w-auto" />
              </label>

              {false && paymentMethod === "paypal" && phase === "payment" && (
                <div className="rounded-xl bg-cream p-4" data-testid="paypal-payment-area">
                  {paymentFailed ? (
                    <PaymentFailedPanel onRetry={retryPayment} t={t} />
                  ) : (
                    <PayPalPayment key={paymentKey} orderId={order.id} onSuccess={onPaypalSuccess} onFailure={onPaymentFailure} />
                  )}
                </div>
              )}
            </div>

            <p className="mt-4 flex items-start gap-2 rounded-xl bg-navy/5 p-3 text-xs text-navy/70">
              <ShieldCheck size={16} className="mt-0.5 shrink-0 text-palm-500" />
              <span><strong className="text-navy">{t("checkout.secureCheckout")}.</strong> {t("checkout.secureCheckoutDesc")}</span>
            </p>
          </section>
        </form>

        <div className="hidden h-fit lg:sticky lg:top-24 lg:block">
          <div className="rounded-2xl border border-border bg-cream-card p-6 card-shadow">
            <h2 className="mb-4 font-head text-lg font-bold text-navy">{t("checkout.orderSummary")}</h2>
            <OrderSummaryBody items={order?.items || usableQuote?.items || items} L={L} t={t} display={display} />
            {phase === "form" ? (
              <button type="submit" form="checkout-form" disabled={submitting || !formValid} className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold disabled:opacity-60" data-testid="place-order-btn">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {submitting ? "..." : ctaLabel}
              </button>
            ) : (
              <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-navy/60">
                <Lock size={13} /> {lang === "fr" ? "Terminez le paiement ci-dessus." : "Complete your payment above."}
              </p>
            )}
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck size={13} className="text-palm-500" /> {t("checkout.secureCheckout")}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: sticky full-width CTA so Continue-to-Payment/Pay is always reachable without scrolling to the (desktop-only) sidebar */}
      {phase === "form" && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-cream-card/95 px-4 py-3 backdrop-blur lg:hidden" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[11px] text-muted-foreground">{t("common.total")}</p>
              <p className="font-head text-base font-bold text-navy">{display.total == null ? "\u2014" : money(display.total)}</p>
            </div>
            <button type="submit" form="checkout-form" disabled={submitting || !formValid} className="btn-gold flex flex-[2] items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold disabled:opacity-60" data-testid="place-order-btn-mobile">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {submitting ? "..." : ctaLabel}
            </button>
          </div>
        </div>
      )}
      {/* Spacer so the fixed mobile bar never overlaps the last section */}
      {phase === "form" && <div className="h-24 lg:hidden" aria-hidden="true" />}
    </div>
  );
}

function OrderSummaryBody({ items, L, t, display }) {
  return (
    <>
      <div className="mb-4 max-h-56 space-y-3 overflow-y-auto">
        {items.map((it) => (
          <div key={it.product_id} className="flex gap-3">
            <img src={it.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
            <div className="flex flex-1 flex-col text-sm">
              <span className="font-medium text-navy line-clamp-1">{L(it, "name")}</span>
              <SellingUnit product={it} />
                {it.selling_unit !== "marmite" && it.weight && <span className="text-xs text-muted-foreground">{it.weight}</span>}
              <span className="text-muted-foreground">×{it.quantity}</span>
            </div>
            <span className="text-sm font-semibold text-navy">{money(it.price * it.quantity)}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2 border-t border-border pt-4 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">{t("common.subtotal")}</span><span className="font-semibold text-navy">{money(display.subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">{t("common.shipping")}</span><span className="font-semibold text-navy">{display.shipping == null ? t("shipping.pending") : display.shipping === 0 ? t("common.free") : money(display.shipping)}</span></div>
        {display.discount > 0 && <div className="flex justify-between text-palm-500"><span>{t("common.discount")}</span><span className="font-semibold">-{money(display.discount)}</span></div>}
        {display.tax > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("common.tax")}</span><span className="font-semibold text-navy">{money(display.tax)}</span></div>}
      </div>
      <div className="mt-4 flex justify-between border-t border-border pt-4">
        <span className="font-head font-bold text-navy">{t("common.total")}</span>
        <span className="font-head text-xl font-bold text-navy" data-testid="checkout-total">{display.total == null ? "\u2014" : money(display.total)}</span>
      </div>
    </>
  );
}

function PaymentFailedPanel({ onRetry, t }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center" data-testid="payment-failed-panel">
      <p className="mb-2 text-sm font-semibold text-red-600">{t("checkout.paymentFailed")}</p>
      <button onClick={onRetry} className="rounded-full bg-red-600 px-5 py-2 text-xs font-bold text-white">{t("checkout.tryAgain")}</button>
    </div>
  );
}
